import React, { useEffect, useRef, useState } from 'react';
import { differenceInCalendarDays, format, parseISO } from 'date-fns';
import { Check, Clock, Loader2, MessageSquare, Trash2, X } from 'lucide-react';
import { Avatar } from '../ui/Avatar';
import { StatusBadge } from '../ui/StatusBadge';
import { useApp } from '../../context/AppContext';
import apiService from '../../services/api';
import toast from 'react-hot-toast';

const NAVY = 'var(--text)';
const DEFAULT_TYPE_COLOR = '#ffc81e';

// ─── Helpers ─────────────────────────────────────────────────
const dateOnly = (value) => (value ? String(value).slice(0, 10) : '');

function isHalfDayRequest(request) {
  if (!request) return false;
  if (request.halfDay === true || request.isHalfDay === true) return true;
  if (request.halfDayPeriod) return true;
  if (request.period === 'AM' || request.period === 'PM') return true;
  if (request.days !== undefined && request.days !== null && parseFloat(request.days) === 0.5) return true;
  return false;
}

function getHalfDayPeriod(request) {
  const period = request?.halfDayPeriod ?? request?.period ?? null;
  if (!period) return null;
  const p = String(period).toLowerCase();
  if (p === 'am' || p === 'morning') return 'AM';
  if (p === 'pm' || p === 'afternoon') return 'PM';
  return null;
}

/** "6 – 9 Oct 2026", "28 Sep – 2 Oct 2026" or "Tue 6 Oct 2026" */
function formatRange(fromStr, toStr) {
  const from = parseISO(fromStr);
  const to = parseISO(toStr || fromStr);
  if (fromStr === toStr || !toStr) return format(from, 'EEE d MMM yyyy');
  if (from.getFullYear() !== to.getFullYear()) return `${format(from, 'd MMM yyyy')} – ${format(to, 'd MMM yyyy')}`;
  if (from.getMonth() === to.getMonth()) return `${format(from, 'd')} – ${format(to, 'd MMM yyyy')}`;
  return `${format(from, 'd MMM')} – ${format(to, 'd MMM yyyy')}`;
}

/** Second line under the dates: when it is, relative to today. */
function whenLabel(fromStr, toStr) {
  const today = new Date();
  const start = differenceInCalendarDays(parseISO(fromStr), today);
  const end = differenceInCalendarDays(parseISO(toStr || fromStr), today);
  if (start <= 0 && end >= 0) return { text: 'On leave now', tone: 'now' };
  if (start === 1) return { text: 'Starts tomorrow', tone: 'soon' };
  if (start > 1 && start <= 14) return { text: `Starts in ${start} days`, tone: 'soon' };
  if (start > 14) return { text: format(parseISO(fromStr), "EEEE"), tone: 'later' };
  if (end === -1) return { text: 'Ended yesterday', tone: 'past' };
  return { text: `Ended ${format(parseISO(toStr || fromStr), 'd MMM')}`, tone: 'past' };
}

const WHEN_STYLE = {
  now:   'text-[color:var(--ok-text)]',
  soon:  'text-[color:var(--warn-text)]',
  later: 'text-sand-500',
  past:  'text-sand-400',
};

// ─── Small pieces ────────────────────────────────────────────
function TypePill({ type, color }) {
  const c = color || DEFAULT_TYPE_COLOR;
  return (
    <span
      className="inline-flex max-w-[180px] items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold"
      style={{ background: `${c}33`, color: 'var(--text)' }}
      title={type}
    >
      <span className="h-2 w-2 flex-shrink-0 rounded-full" style={{ background: c }} />
      <span className="truncate">{type}</span>
    </span>
  );
}

function DurationPill({ request }) {
  if (request.period === 'custom' || (request.startTime && request.endTime)) {
    const hours = parseFloat(request.hours);
    return (
      <span className="inline-flex flex-col">
        <span className="inline-flex items-center gap-1 text-sm font-semibold text-sand-900">
          <Clock size={12} className="text-sand-400" />
          {Number.isFinite(hours) ? `${hours % 1 === 0 ? hours : hours.toFixed(1)}h` : 'Hours'}
        </span>
        <span className="text-xs text-sand-400">
          {request.startTime || '?'} – {request.endTime || '?'}
        </span>
      </span>
    );
  }

  if (isHalfDayRequest(request)) {
    const period = getHalfDayPeriod(request);
    return (
      <span className="inline-flex items-center overflow-hidden rounded-full border border-[color:var(--warn-border)] bg-[color:var(--warn-bg)] text-xs font-semibold text-[color:var(--warn-text)]">
        <span className="px-2 py-0.5">½ day</span>
        {period && <span className="bg-[#FFC81E] px-1.5 py-0.5 text-[10px] font-bold tracking-wide text-[#1C1A14]">{period}</span>}
      </span>
    );
  }

  const d = parseFloat(request.days);
  if (!Number.isFinite(d)) return <span className="text-sm text-sand-400">—</span>;
  return (
    <span className="text-sm font-semibold text-sand-900">
      {d % 1 === 0 ? d : d.toFixed(1)}
      <span className="ml-1 font-normal text-sand-500">{d === 1 ? 'day' : 'days'}</span>
    </span>
  );
}

function Spinner({ size = 13 }) {
  return <Loader2 size={size} className="animate-spin" aria-hidden="true" />;
}

// ─── Row ─────────────────────────────────────────────────────
// onDeleted(id)  — called after a successful delete so the parent removes the row
// onUpdated(req) — called after approve/reject so the parent can update status in place
// typeColor      — the leave type's colour from company settings (optional)
export function RequestRow({ request, showEmployee = true, typeColor, onUpdated, onDeleted }) {
  const { getEmployee, updateStatus, deleteRequest, currentUser, isAdmin, canApprove } = useApp();

  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [rejectNote, setRejectNote] = useState('');
  const rejectInput = useRef(null);

  useEffect(() => {
    if (showReject) rejectInput.current?.focus();
  }, [showReject]);

  // The delete confirmation quietly cancels itself after a few seconds.
  useEffect(() => {
    if (!confirmDelete) return undefined;
    const t = setTimeout(() => setConfirmDelete(false), 4000);
    return () => clearTimeout(t);
  }, [confirmDelete]);

  const empId = request.empId ?? request.userId;
  const emp = request.employee ?? request.emp ?? getEmployee?.(empId) ?? { id: empId, name: 'Unknown employee' };
  const empName = emp.name ?? (`${emp.firstName ?? ''} ${emp.lastName ?? ''}`.trim() || 'Unknown employee');
  const empDept = emp.department?.name ?? emp.dept ?? '';

  const from = dateOnly(request.from);
  const to = dateOnly(request.to || request.from);
  const when = from ? whenLabel(from, to) : null;

  const isPending = request.status === 'pending';
  const isOwn = currentUser?.id && currentUser.id === empId;
  // Company admins, or the admins of this person's department
  const canDecide = isPending && (canApprove ? canApprove(empId) : isAdmin);
  const canDelete = isAdmin || isOwn; // the API allows the same people
  const busy = approving || rejecting || deleting;
  const columns = showEmployee ? 7 : 6;

  // ── Approve ──────────────────────────────────────────────
  async function handleApprove() {
    if (busy) return;
    setApproving(true);
    try {
      const res = await apiService.approveLeaveRequest(request.id);
      const updated = res?.data ?? res;
      toast.success(`Approved ${empName}'s request`);
      updateStatus?.(request.id, 'approved');
      onUpdated?.({ ...request, ...updated, status: 'approved' });
    } catch (err) {
      toast.error(err?.response?.data?.message || "The request couldn't be approved.");
    } finally {
      setApproving(false);
    }
  }

  // ── Reject ───────────────────────────────────────────────
  async function handleReject() {
    if (busy) return;
    setRejecting(true);
    try {
      const res = await apiService.rejectLeaveRequest(request.id, rejectNote.trim());
      const updated = res?.data ?? res;
      toast.success(`Rejected ${empName}'s request`);
      updateStatus?.(request.id, 'rejected');
      onUpdated?.({ ...request, ...updated, status: 'rejected' });
      setShowReject(false);
      setRejectNote('');
    } catch (err) {
      toast.error(err?.response?.data?.message || "The request couldn't be rejected.");
    } finally {
      setRejecting(false);
    }
  }

  function closeReject() {
    setShowReject(false);
    setRejectNote('');
  }

  // ── Delete (two clicks: the bin, then "Delete") ───────────
  async function handleDelete() {
    if (busy) return;
    setDeleting(true);
    try {
      await apiService.deleteLeaveRequest(request.id);
      toast.success('Request deleted');
      deleteRequest?.(request.id);
      onDeleted?.(request.id);
    } catch (err) {
      toast.error(err?.response?.data?.message || "The request couldn't be deleted.");
      setDeleting(false);
      setConfirmDelete(false);
    }
  }

  return (
    <>
      <tr
        className={`group transition-colors ${showReject ? 'bg-[color:var(--bad-bg)]' : 'hover:bg-sand-50'} ${
          deleting ? 'opacity-50' : ''
        }`}
      >
        {/* Employee */}
        {showEmployee && (
          <td className="relative py-3 pl-5 pr-4">
            {/* Pending rows get an amber edge so they stand out in long lists */}
            {isPending && (
              <span className="absolute inset-y-2 left-0 w-1 rounded-r-full bg-[#FFC81E]" aria-hidden="true" />
            )}
            <div className="flex items-center gap-3">
              <Avatar employee={emp} size="md" />
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold" style={{ color: NAVY }}>
                  {empName}
                  {isOwn && <span className="ml-1.5 text-xs font-medium text-sand-400">(you)</span>}
                </p>
                <p className="truncate text-xs text-sand-400">{empDept || 'No department'}</p>
              </div>
            </div>
          </td>
        )}

        {/* Type */}
        <td className="px-4 py-3">
          <TypePill type={request.type} color={typeColor} />
        </td>

        {/* Dates */}
        <td className="whitespace-nowrap px-4 py-3">
          {from ? (
            <>
              <p className="text-sm font-medium text-sand-800">{formatRange(from, to)}</p>
              {when && <p className={`text-xs ${WHEN_STYLE[when.tone]}`}>{when.text}</p>}
            </>
          ) : (
            <span className="text-sm text-sand-400">—</span>
          )}
        </td>

        {/* Duration */}
        <td className="whitespace-nowrap px-4 py-3">
          <DurationPill request={request} />
        </td>

        {/* Status */}
        <td className="px-4 py-3">
          <StatusBadge status={request.status || 'pending'} />
          {request.status === 'rejected' && request.reviewNote && (
            <p className="mt-1 max-w-[160px] truncate text-xs text-[color:var(--bad-text)]" title={request.reviewNote}>
              {request.reviewNote}
            </p>
          )}
        </td>

        {/* Note */}
        <td className="px-4 py-3">
          {request.note ? (
            <span
              className="inline-flex max-w-[180px] items-start gap-1.5 text-xs text-sand-500"
              title={request.note}
            >
              <MessageSquare size={12} className="mt-0.5 flex-shrink-0 text-sand-300" />
              <span className="line-clamp-2">{request.note}</span>
            </span>
          ) : (
            <span className="text-xs text-sand-300">—</span>
          )}
        </td>

        {/* Actions */}
        <td className="px-4 py-3">
          <div className="flex items-center justify-end gap-1.5" style={{ minWidth: canDecide ? 196 : 72 }}>
            {canDecide && !confirmDelete && (
              <>
                <button
                  type="button"
                  onClick={handleApprove}
                  disabled={busy}
                  className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-[10px] bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition-all hover:bg-emerald-700 active:scale-95 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {approving ? <Spinner size={12} /> : <Check size={13} strokeWidth={2.5} />}
                  {approving ? 'Approving…' : 'Approve'}
                </button>
                <button
                  type="button"
                  onClick={() => (showReject ? closeReject() : setShowReject(true))}
                  disabled={busy}
                  aria-expanded={showReject}
                  className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-[10px] border px-3 py-1.5 text-xs font-semibold transition-all active:scale-95 disabled:cursor-not-allowed disabled:opacity-60 text-[color:var(--bad-text)] ${
                    showReject
                      ? 'border-[color:var(--bad-text)] bg-[color:var(--bad-bg)]'
                      : 'border-[color:var(--field-border)] bg-[color:var(--surface)] hover:bg-[color:var(--bad-bg)]'
                  }`}
                >
                  <X size={13} strokeWidth={2.5} />
                  Reject
                </button>
              </>
            )}

            {canDelete &&
              (confirmDelete ? (
                <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                  <span className="text-xs font-medium text-sand-500">Delete?</span>
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={deleting}
                    className="inline-flex items-center gap-1 rounded-[10px] bg-red-600 px-2.5 py-1.5 text-xs font-semibold text-white transition-all hover:bg-red-700 active:scale-95"
                  >
                    {deleting && <Spinner size={12} />}
                    Delete
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmDelete(false)}
                    disabled={deleting}
                    className="rounded-lg px-2 py-1.5 text-xs font-medium text-sand-500 hover:bg-sand-100"
                  >
                    Keep
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmDelete(true)}
                  disabled={busy}
                  title="Delete permanently"
                  aria-label={`Delete ${empName}'s request`}
                  className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-[10px] text-sand-400 opacity-60 transition-all hover:bg-[color:var(--bad-bg)] hover:text-[color:var(--bad-text)] focus:opacity-100 group-hover:opacity-100 disabled:cursor-not-allowed"
                >
                  <Trash2 size={14} />
                </button>
              ))}
          </div>
        </td>
      </tr>

      {/* Reject reason */}
      {showReject && canDecide && (
        <tr className="bg-[color:var(--bad-bg)]">
          <td colSpan={columns} className="px-5 pb-4 pt-0">
            <div className="flex flex-wrap items-center gap-3 rounded-[16px] border border-[color:var(--border)] bg-[color:var(--surface)] p-3 shadow-sm">
              <div className="min-w-[200px] flex-1">
                <label htmlFor={`reject-${request.id}`} className="mb-1 block text-xs font-semibold text-[color:var(--bad-text)]">
                  Reason for rejecting <span className="font-normal text-sand-400">(optional, {empName.split(' ')[0]} will see it)</span>
                </label>
                <input
                  id={`reject-${request.id}`}
                  ref={rejectInput}
                  type="text"
                  value={rejectNote}
                  maxLength={500}
                  onChange={(e) => setRejectNote(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleReject();
                    if (e.key === 'Escape') closeReject();
                  }}
                  placeholder="e.g. Two people from Sales are already off that week"
                  className="form-input"
                />
              </div>
              <div className="flex items-center gap-2 self-end">
                <button type="button" onClick={closeReject} disabled={rejecting} className="btn-ghost">
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleReject}
                  disabled={rejecting}
                  className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-[12px] bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all hover:bg-red-700 active:scale-95 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {rejecting ? <Spinner /> : <X size={14} strokeWidth={2.5} />}
                  {rejecting ? 'Rejecting…' : 'Reject request'}
                </button>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}